import type { Discount } from '@carvana/merch-contracts';
import {
  type ModalQueueGeneratedOpener,
  type ModalQueueOpenerOptions,
  useModalHistory,
  useModalQueue
} from '@carvana/merch-react-hooks';
import {
  MoreDetailsModalSections,
  MoreDetailsOptions
} from '@carvana/merch-tile';
import deepmerge from 'deepmerge';
import React, { createContext, useCallback, useMemo, useState } from 'react';
import { isNonProd } from '~/constants/environment';
import { useIsMobile } from '~/context/common/DeviceContext';
import type { AppliedFilters } from '~/contracts/common';
import { SeoReviewData } from '~/contracts/common/Seo';
import useDisableModalHistory from '~/hooks/common/useDisableModalHistory';
import type { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';

type WIPModalOptions = {
  vehicleId: number;
};

type ContextType = {
  filtersToSave: AppliedFilters;
  geicoDiscount: Discount | null;
  isAccountModalOpen: boolean;
  isActivationModalOpen: boolean;
  isAddCoApplicantModalOpen: boolean;
  isAnyModalOpen: boolean;
  isAppraisalRefreshModalOpen: boolean;
  isFilterModalOpen: boolean;
  isGeicoDiscountModalOpen: boolean;
  isGeolocationModalOpen: boolean;
  isGreatDealModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isSeoReviewModalOpen: boolean;
  isSampleTermsModalOpen: boolean;
  isSaveSearchModalOpen: boolean;
  isSaveSearchNudgeOpen: boolean;
  isSearchBarFocused: boolean;
  isSearchBarModalOpen: boolean;
  isShippingModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  isSortByModalOpen: boolean;
  isTradeIncentiveModalOpen: boolean;
  isWipNotificationModalOpen: boolean;
  moreDetailsOptions: MoreDetailsOptions;
  seoReview: SeoReviewData | null;
  setAccountModalOpen: ModalQueueGeneratedOpener;
  setActivationModalOpen: ModalQueueGeneratedOpener;
  setAddCoApplicantModalOpen: ModalQueueGeneratedOpener;
  setAppraisalRefreshModalOpen: ModalQueueGeneratedOpener;
  setFilterModalOpen: ModalQueueGeneratedOpener;
  setGeicoDiscountModalOpen: (
    isOpen: boolean,
    discount?: Discount,
    openerOptions?: ModalQueueOpenerOptions
  ) => void;
  setGeolocationModalOpen: ModalQueueGeneratedOpener;
  setGreatDealModalOpen: ModalQueueGeneratedOpener;
  setIsSearchBarFocused: (isFocused: boolean) => void;
  setMoreDetailsModalOpen: (
    isOpen: boolean,
    options?: MoreDetailsOptions,
    openerOptions?: ModalQueueOpenerOptions
  ) => void;
  setSaveSearchModalOpen: (
    isOpen: boolean,
    filters: AppliedFilters,
    openerOptions?: ModalQueueOpenerOptions
  ) => void;
  setSampleTermsModalOpen: ModalQueueGeneratedOpener;
  setSaveSearchNudgeOpen: ModalQueueGeneratedOpener;
  setSearchBarModalOpen: ModalQueueGeneratedOpener;
  setSeoReviewModalOpen: (
    isOpen: boolean,
    options?: SeoReviewData | null,
    openerOptions?: ModalQueueOpenerOptions
  ) => void;
  setShippingModalOpen: ModalQueueGeneratedOpener;
  setSoftPullModalOpen: ModalQueueGeneratedOpener;
  setSortByModalOpen: ModalQueueGeneratedOpener;
  setIsTradeIncentiveModalOpen: ModalQueueGeneratedOpener;
  setWipNotificationModalOpen: (
    isOpen: boolean,
    options?: WIPModalOptions,
    openerOptions?: ModalQueueOpenerOptions
  ) => void;
  wipModalVehicleId: number;
};

export const defaultContext: ContextType = {
  filtersToSave: {},
  geicoDiscount: null,
  isAccountModalOpen: false,
  isActivationModalOpen: false,
  isAddCoApplicantModalOpen: false,
  isAnyModalOpen: false,
  isAppraisalRefreshModalOpen: false,
  isFilterModalOpen: false,
  isGeicoDiscountModalOpen: false,
  isGeolocationModalOpen: false,
  isGreatDealModalOpen: false,
  isMoreDetailsModalOpen: false,
  isSeoReviewModalOpen: false,
  isSampleTermsModalOpen: false,
  isSaveSearchModalOpen: false,
  isSaveSearchNudgeOpen: false,
  isSearchBarFocused: false,
  isSearchBarModalOpen: false,
  isShippingModalOpen: false,
  isSoftPullModalOpen: false,
  isSortByModalOpen: false,
  isTradeIncentiveModalOpen: false,
  isWipNotificationModalOpen: false,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails
  },
  seoReview: null,
  setAccountModalOpen() {},
  setActivationModalOpen() {},
  setAddCoApplicantModalOpen() {},
  setAppraisalRefreshModalOpen() {},
  setFilterModalOpen() {},
  setGeicoDiscountModalOpen() {},
  setGeolocationModalOpen() {},
  setGreatDealModalOpen() {},
  setIsSearchBarFocused() {},
  setMoreDetailsModalOpen() {},
  setSeoReviewModalOpen() {},
  setSampleTermsModalOpen() {},
  setSaveSearchModalOpen() {},
  setSaveSearchNudgeOpen() {},
  setSearchBarModalOpen() {},
  setShippingModalOpen() {},
  setSoftPullModalOpen() {},
  setSortByModalOpen() {},
  setIsTradeIncentiveModalOpen() {},
  setWipNotificationModalOpen() {},
  wipModalVehicleId: 0
};

const ModalContext = createContext<ContextType>(defaultContext);

export default ModalContext;

export type OwnProps = React.PropsWithChildren;

export function ModalContextProvider({ children }: OwnProps) {
  const isMobile = useIsMobile();

  const [currentModalId, generateOpener, modalQueue] =
    useModalQueue(ModalPriorities);
  const [filtersToSave, setFiltersToSave] = useState<AppliedFilters>({});
  const [geicoDiscount, setGeicoDiscount] = useState<Discount | null>(
    defaultContext.geicoDiscount
  );
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);
  const [seoReview, setSeoReview] = useState<SeoReviewData | null>(
    defaultContext.seoReview
  );
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);
  const [wipModalVehicleId, setWipModalVehicleId] = useState(
    defaultContext.wipModalVehicleId
  );

  useModalHistory(modalQueue, {
    disable: useDisableModalHistory(),
    logToConsole: isNonProd
  });

  const {
    setAccountModalOpen,
    setActivationModalOpen,
    setAppraisalRefreshModalOpen,
    setAddCoApplicantModalOpen,
    setFilterModalOpen,
    setGeicoDiscountModalOpen,
    setGeolocationModalOpen,
    setGreatDealModalOpen,
    setMoreDetailsModalOpen,
    setSeoReviewModalOpen,
    setSampleTermsModalOpen,
    setSaveSearchModalOpen,
    setSaveSearchNudgeOpen,
    setSearchBarModalOpen,
    setShippingModalOpen,
    setSoftPullModalOpen,
    setSortByModalOpen,
    setIsTradeIncentiveModalOpen,
    setWipNotificationModalOpen
  } = useMemo(
    () => ({
      setAccountModalOpen: generateOpener(ModalId.Account),
      setActivationModalOpen: generateOpener(ModalId.Activation),
      setAppraisalRefreshModalOpen: generateOpener(ModalId.AppraisalRefresh),
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setFilterModalOpen: generateOpener(ModalId.FilterMenu),
      setGeicoDiscountModalOpen: generateOpener(ModalId.GeicoDiscount),
      setGeolocationModalOpen: generateOpener(ModalId.Geolocation),
      setGreatDealModalOpen: generateOpener(ModalId.GreatDeal),
      setMoreDetailsModalOpen: generateOpener(ModalId.MoreDetails),
      setSeoReviewModalOpen: generateOpener(ModalId.Review),
      setSampleTermsModalOpen: generateOpener(ModalId.SampleTermsModal),
      setSaveSearchModalOpen: generateOpener(ModalId.SaveSearchModal),
      setSaveSearchNudgeOpen: generateOpener(ModalId.SaveSearchNudge),
      setSearchBarModalOpen: generateOpener(ModalId.SearchBar),
      setShippingModalOpen: generateOpener(ModalId.Shipping),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull),
      setSortByModalOpen: generateOpener(ModalId.SortBy),
      setIsTradeIncentiveModalOpen: generateOpener(ModalId.TradeIncentive),
      setWipNotificationModalOpen: generateOpener(ModalId.WIPNotifications)
    }),
    [generateOpener]
  );

  const setSavedSearchModalOpenWithFiltersToSave = useCallback(
    (
      isOpen: boolean,
      filters: Partial<AppliedFilters>,
      openerOptions?: ModalQueueOpenerOptions
    ) => {
      setFiltersToSave(filters);
      setSaveSearchModalOpen(isOpen, openerOptions);
    },
    [setSaveSearchModalOpen]
  );

  const setMoreDetailsModalOpenWithVehicle = useCallback<
    ContextType['setMoreDetailsModalOpen']
  >(
    (
      isOpen: boolean,
      options?: MoreDetailsOptions,
      openerOptions?: ModalQueueOpenerOptions
    ) => {
      if (options) setMoreDetailsOptions(options);
      setMoreDetailsModalOpen(isOpen, openerOptions);
    },
    [setMoreDetailsModalOpen]
  );

  const setSeoReviewModalOpenWithReview = useCallback<
    ContextType['setSeoReviewModalOpen']
  >(
    (
      isOpen: boolean,
      options?: SeoReviewData | null,
      openerOptions?: ModalQueueOpenerOptions
    ) => {
      if (options) setSeoReview(options);
      if (!isOpen) setSeoReview(null);
      setSeoReviewModalOpen(isOpen, openerOptions);
    },
    [setSeoReviewModalOpen]
  );

  const setGeicoDiscountModalOpenWithDiscount = useCallback<
    ContextType['setGeicoDiscountModalOpen']
  >(
    (
      isOpen: boolean,
      discount?: Discount,
      openerOptions?: ModalQueueOpenerOptions
    ) => {
      if (discount) setGeicoDiscount(discount);
      if (!isOpen) setGeicoDiscount(null);
      setGeicoDiscountModalOpen(isOpen, openerOptions);
    },
    [setGeicoDiscountModalOpen]
  );

  const setWipNotificationModalOpenWithId = useCallback<
    ContextType['setWipNotificationModalOpen']
  >(
    (
      isOpen: boolean,
      options?: WIPModalOptions,
      openerOptions?: ModalQueueOpenerOptions
    ) => {
      if (options) setWipModalVehicleId(options.vehicleId);
      setWipNotificationModalOpen(isOpen, openerOptions);
    },
    [setWipNotificationModalOpen]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      filtersToSave: filtersToSave,
      geicoDiscount,
      isAccountModalOpen: currentModalId === ModalId.Account,
      isActivationModalOpen: currentModalId === ModalId.Activation,
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isAnyModalOpen: currentModalId > -1,
      isAppraisalRefreshModalOpen: currentModalId === ModalId.AppraisalRefresh,
      isFilterModalOpen: modalQueue.has(ModalId.FilterMenu),
      isGeicoDiscountModalOpen: currentModalId === ModalId.GeicoDiscount,
      isGeolocationModalOpen: currentModalId === ModalId.Geolocation,
      isGreatDealModalOpen: currentModalId === ModalId.GreatDeal,
      isMoreDetailsModalOpen: isMobile
        ? modalQueue.has(ModalId.MoreDetails)
        : currentModalId === ModalId.MoreDetails,
      isSeoReviewModalOpen: currentModalId === ModalId.Review,
      isSampleTermsModalOpen: currentModalId === ModalId.SampleTermsModal,
      isSaveSearchModalOpen: currentModalId === ModalId.SaveSearchModal,
      isSaveSearchNudgeOpen: currentModalId === ModalId.SaveSearchNudge,
      isSearchBarFocused,
      isSearchBarModalOpen: currentModalId === ModalId.SearchBar,
      isShippingModalOpen: currentModalId === ModalId.Shipping,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      isSortByModalOpen: currentModalId === ModalId.SortBy,
      isTradeIncentiveModalOpen: currentModalId === ModalId.TradeIncentive,
      isWipNotificationModalOpen: currentModalId === ModalId.WIPNotifications,
      moreDetailsOptions,
      seoReview,
      setAccountModalOpen,
      setActivationModalOpen,
      setAddCoApplicantModalOpen,
      setAppraisalRefreshModalOpen,
      setFilterModalOpen,
      setGeicoDiscountModalOpen: setGeicoDiscountModalOpenWithDiscount,
      setGeolocationModalOpen,
      setGreatDealModalOpen,
      setIsSearchBarFocused,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithVehicle,
      setSeoReviewModalOpen: setSeoReviewModalOpenWithReview,
      setSampleTermsModalOpen,
      setSaveSearchModalOpen: setSavedSearchModalOpenWithFiltersToSave,
      setSaveSearchNudgeOpen,
      setSearchBarModalOpen,
      setShippingModalOpen,
      setSoftPullModalOpen,
      setSortByModalOpen,
      setIsTradeIncentiveModalOpen,
      setWipNotificationModalOpen: setWipNotificationModalOpenWithId,
      wipModalVehicleId
    }),
    [
      currentModalId,
      filtersToSave,
      geicoDiscount,
      isMobile,
      isSearchBarFocused,
      modalQueue,
      moreDetailsOptions,
      seoReview,
      setAccountModalOpen,
      setActivationModalOpen,
      setAddCoApplicantModalOpen,
      setAppraisalRefreshModalOpen,
      setFilterModalOpen,
      setGeicoDiscountModalOpenWithDiscount,
      setGeolocationModalOpen,
      setGreatDealModalOpen,
      setIsTradeIncentiveModalOpen,
      setMoreDetailsModalOpenWithVehicle,
      setSampleTermsModalOpen,
      setSaveSearchNudgeOpen,
      setSavedSearchModalOpenWithFiltersToSave,
      setSearchBarModalOpen,
      setSeoReviewModalOpenWithReview,
      setShippingModalOpen,
      setSoftPullModalOpen,
      setSortByModalOpen,
      setWipNotificationModalOpenWithId,
      wipModalVehicleId
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedModalContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <ModalContext.Provider value={generateModalContext(mockValue)}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModalContext() {
  return React.useContext(ModalContext);
}
